// LabTrendsChart — multi-series line chart of the 10 core labs over time.
//
// Data source: GET /api/patients/{pid}/investigation-trends?keys=…&months=…
// The doctor can toggle each series on/off with a chip, and switch the
// window between 6m / 12m / All. Every series stays on the same X axis
// (date) so trends line up chronologically. Uses Recharts' LineChart —
// each series is a separate <Line/> so the tooltip shows every visible
// series' value at the hovered date.
//
// This is intentionally a self-contained component so it can be embedded
// inside any collapsible section without leaking state to the parent.

import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { FlaskConical, Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
} from "recharts";

// Which canonical keys we want to graph, in the order they appear as chips.
// Colour palette is chosen to be readable on white + distinct at small line
// widths (2 px) — no two adjacent hues clash.
const SERIES = [
  // `betterDown`: true → rising value is worsening (colour delta red on ↑).
  { key: "fbs",           label: "FBS",           color: "#db2777", unitHint: "mg/dL",         betterDown: true  },
  { key: "ppbs",          label: "PPBS",          color: "#f97316", unitHint: "mg/dL",         betterDown: true  },
  { key: "hba1c",         label: "HbA1c",         color: "#0ea5e9", unitHint: "%",             betterDown: true  },
  { key: "creatinine",    label: "Creatinine",    color: "#6366f1", unitHint: "mg/dL",         betterDown: true  },
  { key: "egfr",          label: "eGFR",          color: "#8b5cf6", unitHint: "mL/min",        betterDown: false },
  { key: "ldl",           label: "LDL",           color: "#ef4444", unitHint: "mg/dL",         betterDown: true  },
  { key: "hdl",           label: "HDL",           color: "#22c55e", unitHint: "mg/dL",         betterDown: false },
  { key: "tc",            label: "Total Chol.",   color: "#a855f7", unitHint: "mg/dL",         betterDown: true  },
  { key: "triglycerides", label: "Triglycerides", color: "#eab308", unitHint: "mg/dL",         betterDown: true  },
  { key: "tsh",           label: "TSH",           color: "#0d9488", unitHint: "µIU/mL",        betterDown: true  },
];

// Same noise thresholds as the compact Last-Labs delta chips — keeps the
// two views clinically consistent (a change we call "noise" in the panel
// stays "noise" here too).
const DELTA_EPSILON = {
  hba1c: 0.1, fbs: 5, ppbs: 5, ldl: 5, hdl: 3, tc: 5, triglycerides: 8,
  creatinine: 0.05, egfr: 2, tsh: 0.2,
};

// Round a delta to a sensible clinical precision — 1 decimal for HbA1c &
// Creatinine, whole numbers for the rest. Otherwise 3.19999998 sneaks in.
function _roundDelta(key, d) {
  if (key === "hba1c" || key === "creatinine" || key === "tsh") return Math.round(d * 10) / 10;
  return Math.round(d);
}

// Default active chips: the 6 the doctor listed as the "must-have" comparison.
const DEFAULT_ACTIVE = new Set(["fbs", "ppbs", "hba1c", "creatinine", "ldl", "triglycerides"]);

const RANGES = [
  { key: 6,   label: "6m" },
  { key: 12,  label: "12m" },
  { key: 0,   label: "All" },  // 0 → all-time on backend
];

export default function LabTrendsChart({ patientId }) {
  const [months, setMonths] = useState(12);
  const [series, setSeries] = useState({});
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(DEFAULT_ACTIVE);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const keys = SERIES.map((s) => s.key).join(",");
        const r = await api.get(`/patients/${patientId}/investigation-trends?keys=${keys}&months=${months}`);
        if (!cancelled) setSeries(r.data?.series || {});
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Could not load lab trends");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [patientId, months]);

  // Fold every series into a single row per date, so Recharts can plot
  // multiple lines from one flat dataset. Missing values become `null`
  // (Recharts skips them, keeping the trend line continuous).
  const chartRows = useMemo(() => {
    const byDate = new Map();
    for (const s of SERIES) {
      const rows = series[s.key] || [];
      for (const r of rows) {
        const d = (r.date || "").slice(0, 10);
        if (!d) continue;
        if (!byDate.has(d)) byDate.set(d, { date: d });
        byDate.get(d)[s.key] = r.value;
      }
    }
    return Array.from(byDate.values()).sort((a, b) => a.date < b.date ? -1 : 1);
  }, [series]);

  const toggle = (k) => setActive((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const availableKeys = new Set(Object.keys(series || {}));
  const anyData = chartRows.length > 0;

  // Per-series delta from the last two points in the currently-loaded
  // window. Rendered inline on each chip so the trend direction is
  // visible without opening the tooltip. Uses the same clinical
  // direction + epsilon rules as the compact Last-Labs delta chips.
  const deltas = useMemo(() => {
    const out = {};
    for (const s of SERIES) {
      const pts = series[s.key];
      if (!pts || pts.length < 2) continue;
      const last = pts[pts.length - 1]?.value;
      const prev = pts[pts.length - 2]?.value;
      if (last == null || prev == null) continue;
      const raw = last - prev;
      const rounded = _roundDelta(s.key, raw);
      const eps = DELTA_EPSILON[s.key] ?? 0.5;
      const noise = Math.abs(raw) < eps;
      const rising = raw > 0;
      const worsening = s.betterDown ? rising : !rising;
      out[s.key] = { delta: rounded, rising, worsening, noise };
    }
    return out;
  }, [series]);

  return (
    <div className="space-y-3" data-testid="lab-trends-chart">
      {/* Range + series chips */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="flex flex-wrap items-center gap-1.5" data-testid="lab-trends-chips">
          {SERIES.map((s) => {
            const has = availableKeys.has(s.key);
            const isOn = active.has(s.key);
            const d = deltas[s.key];
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => has && toggle(s.key)}
                disabled={!has}
                className={`text-[11px] px-2 py-1 rounded-full border transition-colors flex items-center gap-1
                  ${!has
                    ? "bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed"
                    : isOn
                      ? "border-transparent text-white"
                      : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                style={isOn && has ? { backgroundColor: s.color } : undefined}
                data-testid={`lab-trends-chip-${s.key}`}
                title={has ? `${s.label} — click to toggle${d ? ` · Δ vs previous: ${d.delta > 0 ? "+" : ""}${d.delta}` : ""}` : `No ${s.label} values on file`}
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: has ? s.color : "#cbd5e1" }} />
                {s.label}
                {/* Inline delta — mirrors the compact Last-Labs chip so the
                    doctor gets the same signal in both places. Coloured
                    against the current chip background: on white we tint
                    red/green/slate; when the chip is filled (active), we
                    use white so the icon stays readable. */}
                {d && (
                  <span
                    className={`inline-flex items-center gap-0.5 ml-0.5 tabular ${
                      isOn
                        ? "text-white/90"
                        : d.noise
                          ? "text-slate-400"
                          : d.worsening ? "text-rose-600" : "text-emerald-600"
                    }`}
                    data-testid={`lab-trends-delta-${s.key}`}
                  >
                    {d.noise ? <Minus size={9} strokeWidth={2.5} /> : d.rising ? <TrendingUp size={9} strokeWidth={2.5} /> : <TrendingDown size={9} strokeWidth={2.5} />}
                    <span className="text-[10px]">{d.noise ? "≈" : `${d.delta > 0 ? "+" : ""}${d.delta}`}</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[11px] shrink-0">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setMonths(r.key)}
              className={`px-2.5 py-1 ${months === r.key ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid={`lab-trends-range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="border border-slate-200 rounded-md bg-white p-3 h-72 relative">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-xs text-slate-500 gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading lab trends…
          </div>
        )}
        {err && !loading && (
          <div className="h-full flex items-center justify-center text-xs text-rose-600">{err}</div>
        )}
        {!loading && !err && !anyData && (
          <div className="h-full flex items-center justify-center text-xs text-slate-400 text-center px-6">
            <FlaskConical size={14} className="mr-1" />
            No lab values on file for the selected window. Upload a lab report or type values in the Investigations table above.
          </div>
        )}
        {!loading && !err && anyData && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartRows} margin={{ top: 5, right: 15, left: -15, bottom: 0 }}>
              <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" />
              <XAxis dataKey="date" fontSize={10} tick={{ fill: "#64748b" }} tickFormatter={(d) => (d || "").slice(2)} />
              <YAxis fontSize={10} tick={{ fill: "#64748b" }} allowDecimals />
              <Tooltip
                wrapperStyle={{ fontSize: 11 }}
                labelFormatter={(d) => new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                formatter={(value, name) => {
                  const meta = SERIES.find((s) => s.key === name);
                  return [`${value} ${meta?.unitHint || ""}`.trim(), meta?.label || name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8}
                      formatter={(k) => SERIES.find((s) => s.key === k)?.label || k} />
              {SERIES.filter((s) => active.has(s.key) && availableKeys.has(s.key)).map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
